import React from 'react';
import { RouteAggregatedFeedback } from '../../models/climbing/RouteFeedback';
import { GradeSystem, getGradeAsString } from '../../models/climbing/Grade';
import './RouteFeedbackDisplay.css';

interface RouteFeedbackDisplayProps {
  aggregatedFeedback: RouteAggregatedFeedback;
  showDetails?: boolean;
}

export const RouteFeedbackDisplay: React.FC<RouteFeedbackDisplayProps> = ({
  aggregatedFeedback,
  showDetails = false
}) => {
  const renderStarsDisplay = () => {
    if (aggregatedFeedback.totalStarsCount === 0) return null;

    const { averageStars, totalStarsCount, starsDistribution } = aggregatedFeedback;

    return (
      <div className="feedback-stars-display">
        <div className="stars-summary">
          <div className="average-stars">
            <span className="stars-visual">
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  className={`star ${averageStars && star <= Math.round(averageStars) ? 'filled' : ''}`}
                >
                  ★
                </span>
              ))}
            </span>
            <span className="average-text">
              {averageStars ? averageStars.toFixed(1) : '0'} ({totalStarsCount} rating{totalStarsCount !== 1 ? 's' : ''})
            </span>
          </div>
        </div>

        {showDetails && (
          <div className="stars-distribution">
            {[5, 4, 3, 2, 1].map((rating) => (
              <div key={rating} className="distribution-row">
                <span className="rating-label">{rating}★</span>
                <div className="distribution-bar">
                  <div
                    className="distribution-fill"
                    style={{
                      width: `${totalStarsCount > 0 ? (starsDistribution[rating] / totalStarsCount) * 100 : 0}%`
                    }}
                  ></div>
                </div>
                <span className="distribution-count">({starsDistribution[rating]})</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderGradeSuggestions = () => {
    if (aggregatedFeedback.suggestedGradeCount === 0) return null;

    const { suggestedGradeAverage, suggestedGradeCount, suggestedGradeDistribution } = aggregatedFeedback;

    return (
      <div className="feedback-grade-display">
        <div className="grade-summary">
          <span className="suggested-grade">
            Suggested Grade: <strong>{suggestedGradeAverage ? getGradeAsString(Math.round(suggestedGradeAverage), GradeSystem.FRENCH) : 'N/A'}</strong>
          </span>
          <span className="grade-count">
            ({suggestedGradeCount} suggestion{suggestedGradeCount !== 1 ? 's' : ''})
          </span>
        </div>

        {showDetails && Object.keys(suggestedGradeDistribution).length > 0 && (
          <div className="grade-distribution">
            <h4>Grade Distribution:</h4>
            {Object.entries(suggestedGradeDistribution)
              .sort(([a], [b]) => parseInt(b) - parseInt(a))
              .map(([grade, count]) => (
                <div key={grade} className="grade-distribution-item">
                  <span className="grade-label">Grade {getGradeAsString(parseInt(grade), GradeSystem.FRENCH)}:</span>
                  <span className="grade-count">{count} vote{count !== 1 ? 's' : ''}</span>
                </div>
              ))}
          </div>
        )}
      </div>
    );
  };

  const renderCommentsCount = () => {
    if (aggregatedFeedback.totalCommentsCount === 0) return null;

    return (
      <div className="feedback-comments-display">
        <span className="comments-count">
          💬 {aggregatedFeedback.totalCommentsCount} comment{aggregatedFeedback.totalCommentsCount !== 1 ? 's' : ''}
        </span>
      </div>
    );
  };

  const hasFeedback = 
    aggregatedFeedback.totalStarsCount > 0 || 
    aggregatedFeedback.suggestedGradeCount > 0 || 
    aggregatedFeedback.totalCommentsCount > 0;

  if (!hasFeedback) {
    return (
      <div className="route-feedback-display no-feedback">
        <span className="no-feedback-text">No feedback yet</span>
      </div>
    );
  }

  return (
    <div className="route-feedback-display">
      {renderStarsDisplay()}
      {renderGradeSuggestions()}
      {renderCommentsCount()}
      
      {showDetails && (
        <div className="feedback-metadata">
          <small>Last updated: {new Date(aggregatedFeedback.lastUpdated).toLocaleDateString()}</small>
        </div>
      )}
    </div>
  );
};
